// API Client for making HTTP requests to the v1 API
import PolymorphicField from '../polymorphics/PolymorphicField.js';

class ApiClient {
  BASE_URL = 'http://localhost:3000';
  API_VERSION = 'v1';

  setBase(base, version = this.API_VERSION) {
    this.BASE_URL = base;
    this.API_VERSION = version;
    console.log('Set API', base, version);
  }

  buildUrl(endpoint) {
    return `${this.BASE_URL}/api/${this.API_VERSION}${endpoint}`;
  }

  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  // Generic request method to keep it DRY
  async request(endpoint, options = {}) {
    const { method = 'GET', body = null, requireAuth = true, customHeaders = {} } = options;

    const headers = requireAuth ? this.getAuthHeaders() : { 'Content-Type': 'application/json' };

    // Merge custom headers
    Object.assign(headers, customHeaders);

    const requestOptions = {
      method,
      headers
    };

    if (body) {
      requestOptions.body = JSON.stringify(body);
    }

    const response = await fetch(this.buildUrl(endpoint), requestOptions);
    return this.handleResponse(response);
  }

  async healthCheck() {
    return this.request('/health', { requireAuth: false });
  }

  async getApiInfo() {
    return this.request('', { requireAuth: true });
  }

  async getSchema() {
    return this.request('/schema', { requireAuth: false });
  }

  async getComputedSchema() {
    const raw = await this.getSchema();
    return PolymorphicField.computeSchema(raw);
  }

  async listMovies() {
    return this.request('/movies', { requireAuth: true });
  }

  async getMovie(id) {
    return this.request(`/movies/${id}`, { requireAuth: true });
  }

  async createMovie(data) {
    return this.request('/movies', {
      method: 'POST',
      body: data,
      requireAuth: true
    });
  }

  async updateMovie(id, data) {
    return this.request(`/movies/${id}`, {
      method: 'PUT',
      body: data,
      requireAuth: true
    });
  }

  async deleteMovie(id) {
    return this.request(`/movies/${id}`, {
      method: 'DELETE',
      requireAuth: true
    });
  }

  async getJobStatus(jobId) {
    return this.request(`/jobs/${jobId}`, { requireAuth: true });
  }

  async cancelJob(jobId) {
    return this.request(`/jobs/${jobId}`, {
      method: 'DELETE',
      requireAuth: true
    });
  }

  async makeShot(movieId, shotId) {
    return this.request(`/movies/${movieId}/shots/${shotId}/make`, { requireAuth: true });
  }

  async batchGetMovies(ids) {
    const promises = ids.map(id => this.getMovie(id));
    return Promise.all(promises);
  }

  async searchMovies(query) {
    const params = new URLSearchParams({ q: query });
    return this.request(`/movies?${params}`, { requireAuth: true });
  }

  // Authentication methods
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: credentials,
      requireAuth: false
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: userData,
      requireAuth: false
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
      requireAuth: true
    });
  }

  async getProfile() {
    return this.request('/auth/profile', {
      requireAuth: true
    });
  }

  async updateProfile(data) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: data,
      requireAuth: true
    });
  }

  async validateToken() {
    return this.request('/auth/validate', {
      requireAuth: true
    });
  }

  async getGoogleAuthUrl() {
    return this.request('/auth/google/url', {
      requireAuth: false
    });
  }

  async handleGoogleCallback(code) {
    return this.request('/auth/google/callback', {
      method: 'POST',
      body: { code },
      requireAuth: false
    });
  }
}

export default new ApiClient();
